import { Button, FModal, Icons, Table, TextInput } from '@components';
import { mdiAlertCircleOutline, mdiCheckCircleOutline } from '@mdi/js';
import { ContribuicaoVoluntariaService } from '@services/contribuicao-voluntaria';
import { Field, Formik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { number, object } from 'yup';

const simulacaoSchema = object({
  valorContribuicao: number()
    .float(2, 'Máximo de duas casas decimais')
    .required('Insira um valor a partir de R$200')
    .min(200, 'Valor mínimo permitido: R$200'),
});

export const EmissaoBoletoVoluntarioPage = () => {
  const navigate = useNavigate();
  const matricula = useSelector((state) => state.user.data?.matricula);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [tituloModal, setTituloModal] = useState('');
  const [iconeModal, setIconeModal] = useState('');
  const [mensagemModal, setMensagemModal] = useState('');
  const [dadosContribuicaoVoluntaria, setDadosContribuicaoVoluntaria] =
    useState([]);

  const getDadosContribuicaoVoluntaria = useCallback(async () => {
    const contribuicaoVoluntariaService = new ContribuicaoVoluntariaService();
    const contVolunResponse =
      await contribuicaoVoluntariaService.getListaContribuicaoVoluntaria(
        matricula
      );
    if (contVolunResponse[0].MSG_ERRO === null) {
      setDadosContribuicaoVoluntaria(contVolunResponse);
    }
  }, [matricula]);

  const setDadoContribuicao = async (valor) => {
    const contribuicaoVoluntariaService = new ContribuicaoVoluntariaService();
    const response =
      await contribuicaoVoluntariaService.setContribuicaoVoluntaria(
        matricula,
        valor,
        matricula
      );
    if (!response.MSG_ERRO) {
      setTituloModal('Operação realizada');
      setMensagemModal('Favor aguardar o envio do boleto por e-mail');
      setIconeModal('success');
      setModalIsOpen(true);
    } else {
      setTituloModal('Operação não realizada');
      setMensagemModal(response.MSG_CONSIST);
      setIconeModal('error');
      setModalIsOpen(true);
    }
    getDadosContribuicaoVoluntaria();
  };

  useEffect(() => {
    getDadosContribuicaoVoluntaria();
  }, [getDadosContribuicaoVoluntaria]);

  return (
    <>
      <Helmet>
        <title>Solicitação de contribuição voluntária</title>
      </Helmet>
      <h1 className="page-title mb-3">
        <strong>Solicitação de contribuição voluntária</strong>
      </h1>
      <hr className="my-4" />
      <div className="d-flex col-3 col-md-2 col-lg-1 justify-content-center">
        <Button
          className="justify-content-center mb-32"
          transparent
          color="orange"
          icon="arrow_back"
          onClick={() => navigate(-1)}
        >
          Voltar
        </Button>
      </div>
      <div className="card">
        <div className="card-body">
          <h2>
            <strong>
              Solicitação de boleto contribuição voluntária (Aporte)
            </strong>
          </h2>
          <div className="row col-12 col-md-8 col-lg-6 mb-4 mt-4">
            <Formik
              enableReinitialize
              initialValues={{ valorContribuicao: '' }}
              validationSchema={simulacaoSchema}
              onSubmit={async (values, { setSubmitting }) => {
                await setDadoContribuicao(values.valorContribuicao);
                setSubmitting(false);
              }}
            >
              {({ handleSubmit, isSubmitting, isValid }) => (
                <form onSubmit={handleSubmit}>
                  <div className="row ">
                    <div className="col-12 col-lg-7">
                      <Field
                        id="valorContribuicao"
                        name="valorContribuicao"
                        label="Valor da contribuição voluntária"
                        tooltip="Valor mínimo de R$ 200,00 (duzentos reais) ou o valor da contribuição obrigatória para participantes cujo valor mensal desta seja inferior ao mínimo estabelecido"
                        type="number"
                        component={TextInput}
                      />
                    </div>

                    <div className="col-12 col-lg-5 mt-2 d-flex align-items-center justify-content-center">
                      <Button
                        transparent
                        color="orange"
                        type="submit"
                        disabled={isSubmitting || !isValid}
                      >
                        Solicitar
                      </Button>
                    </div>
                  </div>
                  <p className="ts-12 mt-2 text-muted">
                    Caso queira realizar uma contribuição voluntária (aporte) no
                    seu Plano, informe o valor desejado e faça a solicitação. O
                    valor mínimo para preenchimento é de R$200,00. Caso a sua
                    contribuição obrigatória mensal seja abaixo deste valor,
                    favor solicitar o pedido através do{' '}
                    <a
                      className="link d-inline fs-6 text-orange"
                      target="_blank"
                      href="https://forluz.neoassist.com/?th=tag_vforluzfixa&openEmail=true&mainCategory=meuPlanoPrevidencia&subCategory=521082"
                      rel="noreferrer"
                    >
                      Fale Conosco.
                    </a>
                  </p>
                </form>
              )}
            </Formik>
          </div>
          <div className="row col-12 col-md-8 col-lg-6 mt-4">
            <h3 className="ts-20 fw-bold text-muted">Histórico</h3>
            <Table
              className=" mt-2 mb-32"
              columns={['Data de vencimento', 'Valor do boleto']}
            >
              {dadosContribuicaoVoluntaria.length > 0 &&
                dadosContribuicaoVoluntaria.map((dado) => {
                  return (
                    <tr key={dado.CD_BARRA}>
                      <td>{dado.DT_DOC1}</td>
                      <td>{`${dado.ESP_MOEDA} ${dado.VR_COB}`}</td>
                    </tr>
                  );
                })}
            </Table>
          </div>
        </div>
      </div>
      <FModal isOpen={modalIsOpen} close onClose={() => setModalIsOpen(false)}>
        <Icons
          path={
            iconeModal === 'success'
              ? mdiCheckCircleOutline
              : mdiAlertCircleOutline
          }
          color={iconeModal === 'success' ? 'var(--success)' : 'var(--error)'}
          size={2}
        />

        <h1 className="ts-20 fw-bold mb-1">{tituloModal}</h1>
        <p className="ts-16">{mensagemModal}</p>
      </FModal>
    </>
  );
};
